import React, { FormEvent, useEffect, useState } from "react";
import Modal from "react-modal";
import ProfilePicture from "../../atoms/ProfilePicture";
import { useHospDocData } from "../../../lib/contexts/HospitalDoctorContext";
import WhiteButton from "../../atoms/buttons/WhiteButton";
import CloseButton from "../../atoms/buttons/CloseButton";
import DarkBlueButton from "../../atoms/buttons/DarkBlueButton";
import HospDetails from "../../molecules/HospDetails";
import ReactDropdown from "react-dropdown";
import {
  generateTimeOptions,
  generateRandomString,
} from "../../../lib/utils/funcs";
import { toast } from "react-toastify";
import {
  getHosptialDetails,
  updateHosptialDetails,
} from "../../../lib/apis/hospital";
import { useUserData } from "../../../lib/contexts/UserContext";
import ReactModal from "react-modal";
import Cropper, { Area } from "react-easy-crop";
import { getDownloadURL, ref, uploadBytesResumable } from "@firebase/storage";
import { storage } from "../../../firebase";

const HospModal = ({
  closeModal,
  modalIsOpen,
  customStyles,
}: {
  closeModal: () => void;
  modalIsOpen: boolean;
  customStyles: any;
}) => {
  const { userData } = useUserData();
  const { hospitalID, hospData, setHospData } = useHospDocData();

  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(hospData?.hospital_name);
  const [phno, setPhno] = useState(hospData?.hospital_contact_number);
  const [email, setEmail] = useState(userData?.email);
  const [address, setAddress] = useState(hospData?.hospital_address);
  const [start, setStart] = useState(hospData?.hospital_timing.split(" - ")[0]);
  const [end, setEnd] = useState(hospData?.hospital_timing.split(" - ")[1]);

  // 🔹 Image upload states
  const [logo, setLogo] = useState(hospData?.logo || "");
  const [imgSrc, setImgSrc] = useState<string | ArrayBuffer | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [visible, setVisible] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // ✅ disable Save while uploading

  useEffect(() => {
    setName(hospData?.hospital_name);
    setPhno(hospData?.hospital_contact_number);
    setEmail(userData?.email);
    setAddress(hospData?.hospital_address);
    setStart(hospData?.hospital_timing.split(" - ")[0]);
    setEnd(hospData?.hospital_timing.split(" - ")[1]);
    setLogo(hospData?.logo || "");
  }, [modalIsOpen, hospData]);

  // 🔹 Select file for logo
  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImgSrc(reader.result);
        setVisible(true);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  // 🔹 Crop complete callback
  const onCropComplete = (croppedArea: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  };

  // 🔹 Upload cropped logo to Firebase
  const uploadCroppedImage = async () => {
    if (!croppedAreaPixels || !imgSrc) return;

    const image = new Image();
    image.src = imgSrc as string;

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      ctx?.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      canvas.toBlob((blob) => {
        if (!blob) return;
        const fileName = hospitalID + "_" + generateRandomString(8);
        const storageRef = ref(storage, `hospital_logos/${fileName}.png`);
        const uploadTask = uploadBytesResumable(storageRef, blob);

        setIsUploading(true); // ✅ disable Save button while uploading

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadStatus(`Upload is ${progress.toFixed(2)}% done`);
          },
          (error) => {
            setUploadStatus(`Upload failed: ${error.message}`);
            setIsUploading(false); // re-enable on error
          },
          () => {
            getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
              setUploadStatus("File successfully uploaded ✅");

              setLogo(downloadURL);

              setHospData((prev) => {
                if (!prev) return prev;
                return { ...prev, logo: downloadURL };
              });

              console.log("Uploaded logo URL:", downloadURL);

              setVisible(false);
              setIsUploading(false); // ✅ re-enable after success
            });
          }
        );
      }, "image/png");
    };
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (hospitalID) {
      const data = {
        hospital_id: hospitalID,
        hospital_name: name?.trim(),
        hospital_timing: start + " - " + end,
        hospital_address: address,
        logo: logo || "",
      };

      console.log("Submitting hospital update data:", data);

      const res = await updateHosptialDetails(data);
      if (res?.status === 200) {
        console.log("Update response:", res.data);
        closeModal();
        setEdit(false);
        toast.success("Updated successfully!");

        const hosp_data = await getHosptialDetails(hospitalID);
        if (hosp_data?.status === 200) {
          console.log("Fetched updated hospital data:", hosp_data.data);
          setHospData(hosp_data.data.result);
        }
      }
    }
  };

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={closeModal}
      style={customStyles}
      ariaHideApp={false}
    >
      {/* Header */}
      <div className="px-6 py-3 flex flex-row items-center justify-between border-b-[0.5px] border-sbBorder">
        <p className="text-dark font-semibold">
          {edit
            ? `Edit ${
                hospData?.category.toLowerCase() === "clinic"
                  ? "Clinic"
                  : "Hospital"
              } Details`
            : `${
                hospData?.category.toLowerCase() === "clinic"
                  ? "Clinic"
                  : "Hospital"
              } Details`}
        </p>
        <CloseButton
          handleClick={() => {
            closeModal();
            setEdit(false);
          }}
        />
      </div>

      {/* Body */}
      <div className="p-0">
        <form onSubmit={handleSubmit} className="mb-0">
          <div className="px-6 py-5 bg-lightGrey max-h-96 overflow-y-auto">
            {edit ? (
              <div className="flex flex-col">
                {/* Profile (Logo) Section */}
                <div className="flex flex-col items-center">
                  {logo ? (
                    <img
                      className="w-20 h-20 rounded-full"
                      src={logo}
                      alt="hospital-logo"
                    />
                  ) : (
                    <ProfilePicture
                      username={hospData?.hospital_name ?? ""}
                      className={"!w-20 !h-20"}
                      is_active={null}
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="mt-2 text-sm"
                    onChange={onSelectFile}
                  />
                  <p
                    className={`text-xs ${
                      uploadStatus.includes("success")
                        ? "text-green-600"
                        : uploadStatus.includes("failed")
                        ? "text-red-600"
                        : uploadStatus.includes("done")
                        ? "text-yellow-500"
                        : "text-gray-500"
                    }`}
                  >
                    {uploadStatus}
                  </p>
                </div>
                <ReactModal
                  isOpen={visible}
                  onRequestClose={() => setVisible(false)}
                  style={customStyles}
                >
                  <div className="h-[60vh] w-[40vw] flex flex-col">
                    {/* Cropper Section */}
                    <div className="flex-1 relative z-0">
                      <Cropper
                        image={String(imgSrc)}
                        crop={crop}
                        zoom={zoom}
                        aspect={1 / 1}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                      />
                    </div>

                    {/* Buttons Section */}
                    <div className="flex justify-center space-x-4 py-4 relative z-50 bg-white">
                      <button
                        type="button"
                        onClick={uploadCroppedImage}
                        className="rounded-lg bg-blue-600 text-white px-4 py-2"
                      >
                        Crop & Upload
                      </button>
                      <button
                        type="button"
                        onClick={() => setVisible(false)}
                        className="rounded-lg bg-gray-400 text-white px-4 py-2"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </ReactModal>

                {/* Other editable fields */}
                <div className="mt-3">
                  <p className="font-light text-modalText text-sm mb-1">
                    Clinic Name
                  </p>
                  <input
                    className="rounded-lg px-3 py-2 border-[0.5px] border-sbBorder w-full"
                    placeholder="Enter clinic name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={23}
                  />
                </div>
                <div className="mt-3">
                  <p className="font-light text-modalText text-sm mb-1">
                    Timings
                  </p>
                  <div className="flex flex-row items-center space-x-3">
                    <ReactDropdown
                      options={generateTimeOptions("morning")}
                      onChange={(selectedOption) =>
                        setStart(selectedOption.value)
                      }
                      value={start}
                      placeholder={hospData?.hospital_timing.split(" - ")[0]}
                      className="w-full text-sm md:text-base"
                    />
                    <p className="text-docDetail">to</p>
                    <ReactDropdown
                      options={generateTimeOptions("morningEnd")}
                      onChange={(selectedOption) =>
                        setEnd(selectedOption.value)
                      }
                      value={end}
                      placeholder={hospData?.hospital_timing.split(" - ")[1]}
                      className="w-full text-sm md:text-base"
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <p className="font-light text-modalText text-sm mb-1">
                    Address
                  </p>
                  <input
                    className="rounded-lg px-3 py-2 border-[0.5px] border-sbBorder w-full"
                    placeholder="Enter address"
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    maxLength={70}
                  />
                </div>
                <div className="mt-3">
                  <p className="font-light text-modalText text-sm mb-1">
                    Phone Number
                  </p>
                  <input
                    className="rounded-lg px-3 py-2 border-[0.5px] border-sbBorder w-full"
                    placeholder="Enter phone number"
                    type="text"
                    value={phno}
                    onChange={(e) => setPhno(e.target.value)}
                    minLength={12}
                    maxLength={12}
                    disabled
                  />
                </div>
                <div className="mt-3">
                  <p className="font-light text-modalText text-sm mb-1">
                    Email Id
                  </p>
                  <input
                    className="rounded-lg px-3 py-2 border-[0.5px] border-sbBorder w-full"
                    placeholder="Enter email id"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled
                  />
                </div>
              </div>
            ) : (
              <HospDetails />
            )}
          </div>

          {/* Footer */}
          <div className="flex flex-row items-center justify-end border-t-[0.5px] border-sbBorder">
            {edit && (
              <WhiteButton
                name="Cancel"
                handleClick={() => setEdit(false)}
                className="w-full h-14 mb-2 mt-2"
              />
            )}

            {edit && (
              <DarkBlueButton
                name="Save"
                type="submit"
                className="w-full h-14 mb-2 mt-2"
                disabled={isUploading} // ✅ disable Save while uploading
              />
            )}
          </div>
        </form>
        <div className="flex justify-end mr-6">
          {!edit && (
            <WhiteButton
              name="Cancel"
              handleClick={closeModal}
              className="w-full h-14 mb-2 mt-2"
            />
          )}
          {!edit && (
            <DarkBlueButton
              name="Edit"
              handleClick={() => setEdit(true)}
              className="w-full h-14 mb-2 mt-2"
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default HospModal;
